import React, { useEffect, useMemo, useState } from "react";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { SurveyRenderer } from "../../../components/get-compliant/survey/SurveyRenderer";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { useOrganizationVendors, usePrimaryOrganizationId } from "../../../hooks";
import { sellingSurvey } from "../../../surveys/sellingSurvey";
import { GetCompliantContainer } from "../GetCompliantContainer";
import { SELLING_AND_SHARING_SURVEY_NAME } from "./shared";
import { useSellingAndSharingAnswerEffects } from "./useSellingAndSharingAnswerEffects";
import { findAlwaysSellingVendors, isShopifyAudiences } from "../../../util/vendors";

export const VendorSelling = () => {
  // Variables
  const organizationId = usePrimaryOrganizationId();

  // Requests
  const [vendors, vendorsRequest, refreshVendors] = useOrganizationVendors(organizationId);

  // Track if Shopify Audiences was present on first vendor load. We don't want to disable this question when the vendor
  // is created as a downstream effect in this case (to allow user to change their answer).
  const [hadShopifySNIBeforeSurvey, setHadShopifySNIBeforeSurvey] = useState<boolean>(false);
  const [hasSetShopifySNI, setHasSetShopifySNI] = useState<boolean>(false);

  useEffect(() => {
    if (!hasSetShopifySNI && vendorsRequest?.success && Array.isArray(vendors)) {
      const found = vendors.some((v) => isShopifyAudiences(v));
      setHadShopifySNIBeforeSurvey(found);
      setHasSetShopifySNI(true);
    }
  }, [vendors, vendorsRequest, hasSetShopifySNI]);

  // save/load current questions/answers
  const {
    data: survey,
    remote: remoteSurvey,
    answers,
    setAnswer,
    request: surveyQuestionsRequest,
  } = useSurvey(organizationId, SELLING_AND_SHARING_SURVEY_NAME, refreshVendors);

  // todo: pol-3826 move this and useSellingAndSharingAnswerEffects() automatic classification/overrides to backend
  const changes = {};

  if (findAlwaysSellingVendors(vendors).length > 0) {
    if (answers["disclosure-for-selling"] !== "true") {
      changes["disclosure-for-selling"] = "true";
    }
    if (answers["selling-upload-data"] !== "true") {
      changes["selling-upload-data"] = "true";
    }
  }

  useSellingAndSharingAnswerEffects(organizationId, answers, setAnswer, changes);

  const surveyDefinition = useMemo(
    () => sellingSurvey(answers, vendors, { hadShopifySNIBeforeSurvey }),
    [answers, vendors, hadShopifySNIBeforeSurvey],
  );
  return (
    <GetCompliantPageWrapper requests={[vendorsRequest, surveyQuestionsRequest]}>
      <PageHeader titleContent="Data Selling" />

      <GetCompliantContainer>
        <SurveyRenderer
          definition={surveyDefinition}
          survey={survey}
          remote={remoteSurvey}
          setAnswer={setAnswer}
          offsetFirst
          skipCheckboxesAndVisibilityCheckForPrepareSurvey={
            true
          } /* Workaround for survey not rendering correctly when some questions are disabled because they are auto-answered and locked */
        />
      </GetCompliantContainer>
    </GetCompliantPageWrapper>
  );
};
